
import 'custom-event-polyfill';

// spinnerLocation will store a reference to our spinner's GIF file
let spinnerLocation;
let xhrResponse;
let containersCache;

/**
 * @method getDispatcherElement
 * @description checks to see if DispatcherElement exists and creates it if necessary
 * @returns Node
 */
function getDispatcherElement() {
  /**
   * @const dispatcherDataAttribute
   * @description data attribute that our DispatcherElement will have
   */
  const dispatcherDataAttribute = 'sparta-event-dispatcher';

  /**
   * @const dispatcherElementSelector
   * @description selector that will be used to determine if our DispatcherElement already exists
   */
  const dispatcherElementSelector = `[data-${dispatcherDataAttribute}]`;

  /**
   * @const element
   * @description selected DispatcherElement from document
   */
  const element = document.querySelector(dispatcherElementSelector);

  // if DispatcherElement does not yet exist
  if (!element) {
    /**
     * @const generatedElement
     * @description created element to become our DispatcherElement
     */
    const generatedElement = document.createElement('div');

    // set our DispatcherElement data attribute
    generatedElement.setAttribute(`data-${dispatcherDataAttribute}`, true);

    // get current script path and use it to produce relevant spinner path
    const scripts = document.getElementsByTagName('script');

    let currentScriptPath = scripts[scripts.length - 1].src.split('/');

    currentScriptPath[currentScriptPath.length - 1] = 'loading.gif';

    spinnerLocation = currentScriptPath.join('/');

    return generatedElement;
  }

  // return selected DispatcherElement element
  return element;
}

const EVENTS = {
  ERROR_LOADING_WIDGET: "errorLoadingWidget",
  REFRESH_WIDGET: "refreshWidget"
}

/**
 * @class WidgetLoader
 * @description class for our WidgetLoader that will initialize widgets and facilitate communication between widget and page
 */
class WidgetLoaderClass {
  /**
   * @method constructor
   * @description initializes a new instance of WidgetLoader and makes sure our DispatcherElement exists
   */
  constructor() {
    this.dispatcherElement = getDispatcherElement();
  }

  /**
   * @method pub
   * @description creates and dispatches a native CustomEvent on our DispatcherElement
   * @param {String} eventName the event name to dispatch
   * @param {*} [detail={}] the event data to attach to the event.detail parameter
   */
  pub(eventName, detail = {}) {
    /**
     * @const evt
     * @description native CustomEvent to dispatch
     */
    const evt = new CustomEvent(eventName, { detail });

    // dispatch our CustomEvent from our DispatcherElement
    this.dispatcherElement.dispatchEvent(evt);
  }

  /**
   * @method sub
   * @description attaches an event listener to our DispatcherElement
   * @param {String} eventName the event name to listen for
   * @param {Function} callback the event handler to fire
   */
  sub(eventName, callback) {
    // add event listener to DispatcherElement
    this.dispatcherElement.addEventListener(eventName, callback);
  }

  /**
   * @method unsub
   * @description allows subscriber to unsubscribe from event
   * @param {String} eventName the event name to listen for
   * @param {Function} callback the event handler to fire
   */
  unsub(eventName, callback) {
    this.dispatcherElement.removeEventListener(eventName, callback);
  }

  /**
   * @method once
   * @description allows subscriber to listen to one instance of event and then unsub
   * @param {String} eventName the event name to listen for
   * @param {Function} callback the event handler to fire
   */
  once(eventName, callback) {
    const fn = (e) => {
      this.dispatcherElement.removeEventListener(eventName, fn);

      callback(e);
    };

    this.dispatcherElement.addEventListener(eventName, fn);
  }

  /**
   * @method load
   * @description loads a widget into the specified container
   * @param {Object} Config object, must contain keys 'name, version, container, path', optional spinner Boolean value
   */
  load({ name, version, container, path, spinner, options = {}, language='en' }) {
    if (!name || !version || !container) {
      console.error('invalid config object passed to widget loader');
      return false;
    }

    let containersById = [];
    let containerNodes = [];

    // check if loading widget in multiple places
    if (Object.prototype.toString.call(container) === "[object Array]") {
      window.sparta.widgetLoader[name] = {[version]:{}};
      containersById = container;
    } else {
      containersById.push(container);
    }

    // loop over the containers, check if they exist and load as appropriate
    containersById.forEach((currentContainer) => {
      // window.sparta.widgetLoader[name][version][currentContainer] = {loaded:false};

      // get Node for widget container
      const dest = document.getElementById(currentContainer);

      // if widget container does not exist
      if (!dest) {
        console.error('widget container destination does not exist');
        return false;
      }

      containerNodes.push(dest);
    })

    // refresh widget if it exists, otherwise load it
    if (document.querySelectorAll('[data-sparta-container="' + name + '"][data-version="' + version + '"]').length > 0) {
      this.pub(EVENTS.REFRESH_WIDGET, {name:name, version:version, container:containerNodes});
    } else {
      this.generateStyleSheet(containerNodes, spinner);
      this.widgetXHR(containerNodes, path, name, version, options, language);
    }
  }

  /**
   * @method generatedStyleSheet
   * @description add stylesheet for loading states so we don't need another HTTP request
   * @param {Array} containers array of widget container Nodes
   * @param {Boolean} [spinner=true] should we use default spinner
   */
  generateStyleSheet(containers, spinner = true) {
    containers.forEach((dest) => {
      // generate stylesheet for loading
      const style = document.createElement('style');

      let styles = `
        #${dest.id} > [data-sparta-container].sparta-widget-loading,
        #${dest.id} > [data-sparta-container].sparta-widget-loading > .sparta-widget {
          visibility: hidden;
          height: 100%;
          widht: 100%;
        }
      `;

      // if we are supposed to show our spinner, add a style to the widget container
      if (spinner) {
        styles += `
          #${dest.id}.sparta-widget-loading {
            height: 100%;
            background-image: url('${spinnerLocation}');
            background-repeat: no-repeat;
            background-position: center center;
          }
        `;
      }

      // add stylesheet to page
      style.type = 'text/css';
      style.id = `${dest.id}-sparta-widget-style-sheet`;

      if (style.stylesSheet) {
        style.styleSheet.cssText = styles;
      } else {
        style.appendChild(document.createTextNode(styles));
      }

      document.head.appendChild(style);

      dest.classList.add('sparta-widget-loading');
    })
  }

  /**
   * @method widgetXHR
   * @description loads widget via AJAX request
   * @param {Array} containers array of widget container Nodes
   * @param {String} path where to load the widget from
   * @param {String} name the name of the widget
   * @param {String} version the version number of the widget to load
   * @param {String} language the language to attempt to load (en default)
   */
  widgetXHR(containers, path, name, version, options, language) {
    function xhrFunc(responseText, containersCache, wasCached) {
      if (wasCached) {
        containersCache.forEach((currentContainer) => {
          currentContainer.innerHTML = responseText;
        });

        const initArray = [];
        const moduleArray = document.querySelectorAll('[data-component="module"]');
        moduleArray.forEach((element) => {
          if (element.dataset.module.indexOf('module') !== -1) {
            const dataset = element.dataset;
            initArray.push(window.sparta.require[name][version].require(`modules/${dataset.moduleRef}/${dataset.version}/js/${dataset.module}`));
          }
        });

        const bootstrap = window.sparta.require[name][version].require('global/sparta-bootstrap-utility');
        const widget = window.sparta.require[name][version].require(`modules/${name}/${version}/js/${name}`);
        initArray.push(widget);
        bootstrap.default.init(initArray, { name, version });
      } else {
        // place HTML in all containers
        containers.forEach((currentContainer) => {
          currentContainer.innerHTML = responseText;
        });

        // grab the last item and load the JS into that
        let dest = containers[containers.length - 1];

        // add options into meta data for bootstrapper
        dest.querySelector("[data-sparta-widget]").setAttribute("data-sparta-options", JSON.stringify(options));

        // remove loader
        dest.style.height = 'auto';
        dest.style.backgroundImage = '';

        // get all script tags so we can make the scripts load
        let myScripts = [].slice.call(dest.querySelectorAll('script'));
        // iterate through scripts
        myScripts.forEach((item) => {
          // skip babel polyfill
          if (options.skipBabelPolyFill && item.src.indexOf("babel-polyfill.js") !== -1) {
            return;
          }

          // generate new script element
          let newElem = document.createElement('script');

          // depending on script type
          if (item.src) {
            // update src attribute
            newElem.src = item.src;
          } else {
            // inject code into script innerHTML
            newElem.innerHTML = item.innerHTML;
          }

          // add script tag to widget container
          dest.appendChild(newElem);

          // remove original script node (ie10 does not support item.remove())
          item.parentNode.removeChild(item);
        });
      }
    }

    if (xhrResponse) {
      xhrFunc(xhrResponse, containersCache, true);
      return;
    }

    const xhrReq = new XMLHttpRequest();
    // xhrReq.withCredentials = true;

    const LANG_PATH = (!language || language.toLowerCase() === "en") ? "" : `${language.toLowerCase()}/`;
    const WIDGET_PATH = `${path}/spa/widgets/${name}/${version}/${LANG_PATH}index.html`;
    const self = this;

    // execute AJAX request
    xhrReq.open('GET', WIDGET_PATH);

    xhrReq.onreadystatechange = function () {
      if (this.readyState === 4) {
        if (this.status !== 200) {
          console.error('unable to load widget:', WIDGET_PATH, this.status);

          containers.forEach((dest) => {
            dest.innerHTML = 'unable to load widget';
            self.pub(EVENTS.ERROR_LOADING_WIDGET, {widget:{name:name, version:version, path:path, container:dest}, xhrReq:this});
          });
        } else {
          xhrResponse = this.responseText;
          containersCache = containers;
          xhrFunc(this.responseText, containersCache, false);
        }
      }
    };

    xhrReq.send();
  }
}

if (!(window.sparta && window.sparta.widgetLoader)) {
  const widgetLoader = new WidgetLoaderClass();

  window.sparta = window.sparta || {};

  window.sparta.widgetLoader = widgetLoader;

  document.dispatchEvent(new CustomEvent('spartaLoaderLoaded'));
}




// WEBPACK FOOTER //
// ./src/index.js